import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import {
  AccountsListPageDocument,
  BankingOnboardingFragment,
  ConnectBankDocument,
  CreateBankingManualAccountDocument,
  CreateBankingOnboardingDocument,
  CreateForCardsBankingOnboardingDocument,
  CurrencySelectorCurrencyFragment,
  LegalEntityForSelectFragment,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCallback, useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { defaultBankNumberFormat } from 'modules/banking/lib/bankAccountNumbersHelpers'
import { getDefaultAlias } from 'modules/banking/utils'

import { WizardData } from './types'
import { BankAccountConnectionWizardStep } from './wizardSteps/BankAccountConnectionWizardStep'
import { BankAccountManualInformationWizardStep } from './wizardSteps/BankAccountManualInformationWizardStep'
import { BankAccountSelectorWizardStep } from './wizardSteps/BankAccountSelectorWizardStep'
import { BankAccountTypeSelectorWizardStep } from './wizardSteps/BankAccountTypeSelectorWizardStep'
import { ExternalOnboardingExplanationStep } from './wizardSteps/ExternalOnboardingExplanationStep'
import { LedgerAccountLinkStep } from './wizardSteps/LedgerAccountLinkStep'
import { LegalEntityWizardStep } from './wizardSteps/LegalEntityWizardStep'
import { MembershipsStep } from './wizardSteps/MembershipsStep'

interface NewAccountModalProps {
  onClose: () => void
  legalEntities: LegalEntityForSelectFragment[]
  currencies: CurrencySelectorCurrencyFragment[]
  accountingEnabled: boolean
  existingOnboardings: BankingOnboardingFragment[]
  showAccountPurposeSelector: boolean
}

export const NewAccountModal = ({
  onClose,
  legalEntities,
  currencies,
  accountingEnabled,
  existingOnboardings,
  showAccountPurposeSelector,
}: NewAccountModalProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [showConnectStep, setShowConnectStep] = useState<boolean>(false)
  const [showConnectionModal, setShowConnectionModal] = useState<boolean>(false)
  const [onboardingUrl, setOnboardingUrl] = useState<string | null>(null)
  const [selectedCreationSource, setSelectedCreationSource] = useState<string | null>(null)
  const [accountNumberErrors, setAccountNumberErrors] = useState<{
    accountNumber?: string
    complementaryData?: string
  }>({})

  const handleClose = (id: number) => {
    if (accountingEnabled) {
      navigate(navigation.finance.banking.accounts.accountId.fullPath({ id }))
    } else {
      onClose()
      navigate(navigation.finance.banking.accounts.fullPath())
    }
  }

  const trackAddAccount = (account_type: 'manual' | 'automatic', hasEmployees?: boolean) => {
    track(trackingEvents.accounting.banking.addBankAccount, { account_type })
    if (hasEmployees) track(trackingEvents.accounting.banking.addPeopleBankAccount)
  }

  const isBankAccountDataCompleted = useCallback((wizardData: WizardData): boolean => {
    if (wizardData.institution) {
      return !!wizardData.institution && !!wizardData.institution.id
    }
    const validNumber = !!wizardData.accountNumber && !!wizardData.accountNumberType
    return validNumber && !!wizardData.legalEntity && !!wizardData.currency
  }, [])

  const [createManualAccount] = useMutation(CreateBankingManualAccountDocument, {
    extractorFn: (data) => data.bankingMutations.createManualBankingBankAccount,
    refetchQueries: [AccountsListPageDocument],
  })

  const [connectBank] = useMutation(ConnectBankDocument, {
    extractorFn: (data) => data.bankingMutations.createForExternalBankBankingOnboarding,
    refetchQueries: [AccountsListPageDocument],
  })

  const [createForCards] = useMutation(CreateForCardsBankingOnboardingDocument, {
    extractorFn: (data) => data.bankingMutations.createForCardsBankingOnboarding,
    refetchQueries: [AccountsListPageDocument],
  })

  const [createForPayments] = useMutation(CreateBankingOnboardingDocument, {
    extractorFn: (data) => data.bankingMutations.createForPaymentsBankingOnboarding,
    refetchQueries: [AccountsListPageDocument],
  })

  const redirectToBankAggregationProvider = useUnsafeCallback(() => {
    if (!onboardingUrl) return
    window.open(onboardingUrl, '_self')
  }, [onboardingUrl])

  const isUnhandledResult = (value: unknown): value is UnhandledResult<Result<unknown>> => {
    return value !== null && typeof value === 'object'
  }

  const handleOnboardingResult = useUnsafeCallback(
    (result: unknown, creationSource: string) => {
      if (!isUnhandledResult(result)) {
        return
      }
      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('banking.accounts.new_wizard.notifications.error'),
        })
        app.setProgress(null)
      })

      if (
        handled.success &&
        handled.data &&
        typeof handled.data === 'object' &&
        'onboarding' in handled.data
      ) {
        const dataObj = handled.data
        const onboarding = 'onboarding' in dataObj ? dataObj.onboarding : null
        const newOnboardingUrl =
          onboarding &&
          typeof onboarding === 'object' &&
          'onboardingUrl' in onboarding &&
          typeof onboarding.onboardingUrl === 'string'
            ? onboarding.onboardingUrl
            : null
        if (newOnboardingUrl) {
          app.setProgress(null)

          // For Stripe/Swan, redirect directly (we already showed the explanation step)
          if (
            creationSource === 'expenses' ||
            creationSource === 'software' ||
            creationSource === 'benefits' ||
            creationSource === 'payments'
          ) {
            window.open(newOnboardingUrl, '_self')
          } else {
            // For GoCardless, show the modal with the redirect button (no auto-redirect)
            setOnboardingUrl(newOnboardingUrl)
            setShowConnectionModal(true)
          }
        }
      }
    },
    [setOnboardingUrl, setShowConnectionModal]
  )

  const handleSubmit = async (data: WizardData) => {
    // Handle automatic account creation based on creationSource
    if (data.legalEntity && data.creationSource) {
      app.setProgress({
        message: i18n.t('banking.accounts.new_wizard.notifications.connecting_bank'),
      })

      setOnboardingUrl(null)

      // Choose the correct mutation based on creationSource
      if (
        data.creationSource === 'expenses' ||
        data.creationSource === 'software' ||
        data.creationSource === 'benefits'
      ) {
        // For card-based accounts (Stripe)
        const result = await createForCards({
          variables: {
            legalEntityId: data.legalEntity.id,
            source: data.creationSource,
          },
        }).then((res) => {
          trackAddAccount('automatic')
          return res
        })

        handleOnboardingResult(result, data.creationSource)
        return
      } else if (data.creationSource === 'payments') {
        // For payments account (Swan)
        const result = await createForPayments({
          variables: {
            legalEntityId: data.legalEntity.id,
          },
        }).then((res) => {
          trackAddAccount('automatic')
          return res
        })

        handleOnboardingResult(result, data.creationSource)
        return
      } else if (
        (data.creationSource === 'finance' || data.creationSource === 'accounts_payable') &&
        data.institution
      ) {
        const result = await connectBank({
          variables: {
            legalEntityId: data.legalEntity.id,
            institutionId: data.institution.id,
          },
        }).then((res) => {
          trackAddAccount('automatic')
          return res
        })

        handleOnboardingResult(result, data.creationSource)
        return
      }
    }

    // Handle GoCardless connection for legacy flow (when showAccountPurposeSelector is false)
    // In this case, there's no creationSource but there is an institution selected
    if (!data.creationSource && data.institution && data.legalEntity) {
      app.setProgress({
        message: i18n.t('banking.accounts.new_wizard.notifications.connecting_bank'),
      })

      setOnboardingUrl(null)

      const result = await connectBank({
        variables: {
          legalEntityId: data.legalEntity.id,
          institutionId: data.institution.id,
        },
      }).then((res) => {
        trackAddAccount('automatic')
        return res
      })

      handleOnboardingResult(result, 'finance')
      return
    }

    // Handle manual account creation
    if (!data.creationSource && data.legalEntity) {
      app.setProgress({
        message: i18n.t('banking.accounts.new_wizard.notifications.creating'),
      })

      const result = await createManualAccount({
        variables: {
          legalEntityId: String(data.legalEntity.id),
          currency: data.currency,
          accountNumber: data.accountNumber || '',
          accountNumberType: data.accountNumberType,
          complementaryData: data.complementaryData,
          accountAlias: data.accountAlias,
          ledgerAccountId: data.ledgerAccountId,
          employeeSelection: data.employeeSelection,
        },
      }).then((res) => {
        const hasEmployees =
          (data.employeeSelection?.type === 'manual' && data.employeeSelection.ids.length > 0) ||
          data.employeeSelection?.type === 'all'
        trackAddAccount('manual', hasEmployees)
        return res
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('banking.accounts.new_wizard.notifications.error'),
        })
        app.setProgress(null)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('banking.accounts.new_wizard.notifications.success'),
        })
        app.setProgress(null)

        if (handled.data.bankAccount) {
          handleClose(handled.data.bankAccount.id)
        }
      }
    }
  }

  const legalEntityStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.new_wizard.step_1.title'),
      isCompleted: (wizardData) => !!wizardData.legalEntity,
      nextText: i18n.t('banking.accounts.new_wizard.next'),
      render: (props) => {
        const manualData: WizardData = { ...props.data, institution: null }
        return (
          <LegalEntityWizardStep
            data={manualData}
            completed={props.completed}
            loading={props.loading}
            onDataChanged={(newData) => {
              const updatedData = props.data.institution
                ? { ...newData, institution: props.data.institution }
                : newData
              props.onDataChanged(updatedData)
            }}
          />
        )
      },
    }),
    []
  )

  const bankAccountSelectorStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.new_wizard.step_2.title'),
      isCompleted: () => Boolean(true),
      nextText: i18n.t('banking.accounts.new_wizard.next'),
      render: (props) => (
        <BankAccountSelectorWizardStep onSelect={(inst) => setShowConnectStep(!!inst)} {...props} />
      ),
    }),
    []
  )

  const bankAccountManualInformationStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.new_wizard.step_3_0.title'),
      isCompleted: (data) =>
        isBankAccountDataCompleted(data) &&
        accountNumberErrors.accountNumber === undefined &&
        accountNumberErrors.complementaryData === undefined,
      nextText: i18n.t('banking.accounts.new_wizard.next'),
      render: (props) => (
        <BankAccountManualInformationWizardStep
          {...props}
          currencies={currencies}
          errors={accountNumberErrors}
          onErrorsChange={setAccountNumberErrors}
        />
      ),
    }),
    [currencies, isBankAccountDataCompleted, accountNumberErrors]
  )

  const bankAccountConnectionStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.new_wizard.step_3_1.title'),
      isCompleted: isBankAccountDataCompleted,
      nextText: i18n.t('banking.accounts.new_wizard.finish_and_synchronize'),
      render: (props) => <BankAccountConnectionWizardStep {...props} />,
    }),
    [isBankAccountDataCompleted]
  )

  const ledgerAccountLinkStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.finish_wizard.step_2.side_title'),
      isCompleted: isBankAccountDataCompleted,
      nextText: i18n.t('banking.accounts.new_wizard.next'),
      render: (props) => <LedgerAccountLinkStep {...props} />,
    }),
    [isBankAccountDataCompleted]
  )

  const membershipsStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.finish_wizard.step_3.side_title'),
      isCompleted: isBankAccountDataCompleted,
      nextText: i18n.t('banking.accounts.new_wizard.create_account'),
      render: (props) => <MembershipsStep {...props} />,
    }),
    [isBankAccountDataCompleted]
  )

  const bankAccountTypeSelectorStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.new_wizard.step_1_1.title'),
      isCompleted: (wizardData) => !!wizardData.creationSource,
      nextText: i18n.t('banking.accounts.new_wizard.next'),
      render: (props) => {
        // Update selectedCreationSource when data changes
        if (props.data.creationSource !== selectedCreationSource) {
          setSelectedCreationSource(props.data.creationSource)
        }
        return (
          <BankAccountTypeSelectorWizardStep {...props} existingOnboardings={existingOnboardings} />
        )
      },
    }),
    [existingOnboardings, selectedCreationSource]
  )

  const externalOnboardingExplanationStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.new_wizard.external_onboarding.title', {
        provider: selectedCreationSource === 'payments' ? 'Swan' : 'Stripe',
      }),
      isCompleted: () => true,
      nextText: i18n.t('banking.accounts.new_wizard.finish_and_synchronize'),
      render: () => (
        <ExternalOnboardingExplanationStep creationSource={selectedCreationSource || ''} />
      ),
    }),
    [selectedCreationSource]
  )

  const steps = useUnsafeMemo((): WizardStep<WizardData>[] => {
    const isDefined = <T,>(value: T | undefined): value is T => value !== undefined

    // If no creation source selected yet, only show initial steps
    if (showAccountPurposeSelector && !selectedCreationSource) {
      return [legalEntityStep, bankAccountTypeSelectorStep]
    }

    // For GoCardless (finance), we need the bank selection step
    if (
      showAccountPurposeSelector &&
      (selectedCreationSource === 'finance' || selectedCreationSource === 'accounts_payable')
    ) {
      return [
        legalEntityStep,
        bankAccountTypeSelectorStep,
        bankAccountSelectorStep,
        bankAccountConnectionStep,
      ]
    }

    // For Stripe/Swan (expenses, software, benefits, payments), show explanation step before submission
    if (
      showAccountPurposeSelector &&
      (selectedCreationSource === 'expenses' ||
        selectedCreationSource === 'software' ||
        selectedCreationSource === 'benefits' ||
        selectedCreationSource === 'payments')
    ) {
      return [legalEntityStep, bankAccountTypeSelectorStep, externalOnboardingExplanationStep]
    }

    // Fallback to manual account creation (if needed)
    if (accountingEnabled) {
      if (!showAccountPurposeSelector) {
        return showConnectStep
          ? [legalEntityStep, bankAccountSelectorStep, bankAccountConnectionStep]
          : [
              legalEntityStep,
              bankAccountSelectorStep,
              bankAccountManualInformationStep,
              ledgerAccountLinkStep,
              membershipsStep,
            ].filter((n) => n)
      }
      return showConnectStep
        ? [legalEntityStep, bankAccountSelectorStep, bankAccountConnectionStep]
        : [
            legalEntityStep,
            bankAccountTypeSelectorStep,
            bankAccountManualInformationStep,
            ledgerAccountLinkStep,
            membershipsStep,
          ].filter(isDefined)
    } else {
      if (!showAccountPurposeSelector) {
        return [legalEntityStep, bankAccountManualInformationStep, membershipsStep]
      }
      return [
        legalEntityStep,
        bankAccountTypeSelectorStep,
        bankAccountManualInformationStep,
        membershipsStep,
      ].filter(isDefined)
    }
  }, [
    showConnectStep,
    selectedCreationSource,
    showAccountPurposeSelector,
    legalEntityStep,
    bankAccountTypeSelectorStep,
    externalOnboardingExplanationStep,
    bankAccountSelectorStep,
    bankAccountConnectionStep,
    bankAccountManualInformationStep,
    accountingEnabled,
    ledgerAccountLinkStep,
    membershipsStep,
  ])

  const defaultLegalEntity = legalEntities.length === 1 ? legalEntities[0] ?? null : null

  const initialData = useUnsafeMemo<WizardData>(
    () => ({
      legalEntity: defaultLegalEntity,
      currency: defaultLegalEntity?.currency || 'EUR',
      accountNumberType: defaultLegalEntity?.country
        ? defaultBankNumberFormat(defaultLegalEntity.country)
        : defaultBankNumberFormat('es'),
      accountNumber: '',
      accountAlias: getDefaultAlias('finance'),
      complementaryData: '',
      institution: null,
      maxDays: 0,
      search: '',
      country: defaultLegalEntity?.country.toLocaleUpperCase() || null,
      ledgerAccountId: null,
      ledgerAccountName: null,
      creationSource: null,
    }),
    [defaultLegalEntity]
  )

  return (
    <>
      {showConnectionModal && (
        <Modal
          title={i18n.t('banking.accounts.new_wizard.step_3_1.external_redirection')}
          description={i18n.t('banking.accounts.new_wizard.step_3_1.extra_info')}
          illustration={'workflow'}
          locked
          footer={
            <Box flexDirection="row" gap="s8">
              <Button
                disabled={onboardingUrl === null}
                onClick={() => redirectToBankAggregationProvider()}
              >
                {i18n.t('common.continue')}
              </Button>
            </Box>
          }
          disableFocusTrap
          onClose={() => null}
        />
      )}
      <Wizard.Modal
        title={i18n.t('banking.accounts.actions.add_account')}
        icon="add-block"
        description=""
        initialData={initialData}
        defaultStepIndex={legalEntities.length === 1 ? 1 : 0}
        onSubmit={handleSubmit}
        onClose={onClose}
        steps={steps}
      />
    </>
  )
}
